import { useCan } from '@sentinel/web-core';
import { useQuery } from '@tanstack/react-query';
import { isPaused, type BandwidthFeature, type Station } from '@sentinel/shared';
import { endpoints } from '@/core/api/endpoints';
import { keys } from '@/core/query/query-keys';
import { useThisStation } from './this-station';

/** A station that stops calling turns offline on the server's clock: the list is read again
 *  every so often even without events. */
const REFRESH_MS = 30_000;

export function useStations() {
  const canView = useCan('connectivity:view');
  return useQuery({
    queryKey: keys.stations,
    queryFn: endpoints.connectivity.stations,
    enabled: canView,
    refetchInterval: REFRESH_MS,
  });
}

/** The station this browser sits at, as the server sees it now; null: none chosen (or gone). */
export function useStationHere(): Station | null {
  const stationId = useThisStation((s) => s.stationId);
  const stations = useStations();
  if (stationId === null) return null;
  return stations.data?.items.find((s) => s.id === stationId) ?? null;
}

/** Whether a heavy feature is paused here by the bandwidth of this station (ADR 0026). A browser
 *  that chose no station pauses nothing: it behaves as before. */
export function usePausedFeature(feature: BandwidthFeature): Station | null {
  const station = useStationHere();
  return station !== null && isPaused(station.level, feature) ? station : null;
}
